import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ShieldCheck, ClipboardList, AlertTriangle, CheckCircle2, Users } from 'lucide-react';
import { AuditQueue } from './AuditQueue';
import { format, startOfDay, startOfWeek } from 'date-fns';

export function QualityControlDashboard() {
  const { user, hasRole } = useAuth();
  const isLead = hasRole('quality_control_lead');

  const { data: stats, isLoading } = useQuery({
    queryKey: ['qc-stats', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const today = startOfDay(new Date()).toISOString();
      const weekStart = startOfWeek(new Date()).toISOString();

      const [{ count: todayCount }, { count: weekCount }, { count: needsRev }, { count: rejected }] = await Promise.all([
        supabase.from('listing_audits').select('*', { count: 'exact', head: true }).eq('auditor_id', user.id).gte('audited_at', today),
        supabase.from('listing_audits').select('*', { count: 'exact', head: true }).eq('auditor_id', user.id).gte('audited_at', weekStart),
        supabase.from('listing_audits').select('*', { count: 'exact', head: true }).eq('status', 'needs_revision'),
        supabase.from('listing_audits').select('*', { count: 'exact', head: true }).eq('status', 'rejected'),
      ]);

      return {
        today: todayCount || 0,
        week: weekCount || 0,
        needsRevision: needsRev || 0,
        rejected: rejected || 0,
      };
    },
    enabled: !!user,
  });

  const { data: recentAudits } = useQuery({
    queryKey: ['qc-recent-audits'],
    queryFn: async () => {
      const { data } = await supabase
        .from('listing_audits')
        .select('*')
        .order('audited_at', { ascending: false })
        .limit(8);
      return data || [];
    },
  });

  const { data: teamPerf } = useQuery({
    queryKey: ['qc-team-perf'],
    queryFn: async () => {
      if (!isLead) return [];
      const weekStart = startOfWeek(new Date()).toISOString();
      const { data } = await supabase
        .from('listing_audits')
        .select('auditor_id, status')
        .gte('audited_at', weekStart);
      const grouped: Record<string, { total: number; passed: number }> = {};
      (data || []).forEach((a: any) => {
        if (!grouped[a.auditor_id]) grouped[a.auditor_id] = { total: 0, passed: 0 };
        grouped[a.auditor_id].total++;
        if (a.status === 'passed') grouped[a.auditor_id].passed++;
      });
      const ids = Object.keys(grouped);
      if (!ids.length) return [];
      const { data: profiles } = await supabase.from('profiles').select('user_id, full_name').in('user_id', ids);
      return ids.map(id => ({
        id,
        name: profiles?.find(p => p.user_id === id)?.full_name || 'Unknown',
        ...grouped[id],
      }));
    },
    enabled: isLead,
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <ShieldCheck className="h-7 w-7 text-primary" />
          Quality & Data Control
        </h2>
        <p className="text-muted-foreground">Audit live and pending listings for accuracy and compliance</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {isLoading ? (
          [1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)
        ) : (
          <>
            <Card><CardContent className="p-4 flex items-center gap-3"><CheckCircle2 className="h-8 w-8 text-emerald-500" /><div><p className="text-2xl font-bold">{stats?.today || 0}</p><p className="text-xs text-muted-foreground">Audited Today</p></div></CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3"><ClipboardList className="h-8 w-8 text-blue-500" /><div><p className="text-2xl font-bold">{stats?.week || 0}</p><p className="text-xs text-muted-foreground">This Week</p></div></CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3"><AlertTriangle className="h-8 w-8 text-amber-500" /><div><p className="text-2xl font-bold">{stats?.needsRevision || 0}</p><p className="text-xs text-muted-foreground">Needs Revision</p></div></CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3"><AlertTriangle className="h-8 w-8 text-destructive" /><div><p className="text-2xl font-bold">{stats?.rejected || 0}</p><p className="text-xs text-muted-foreground">Rejected</p></div></CardContent></Card>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <AuditQueue />
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Recent Audits</CardTitle></CardHeader>
            <CardContent>
              {!recentAudits?.length ? (
                <p className="text-sm text-muted-foreground text-center py-6">No recent audits</p>
              ) : (
                <div className="space-y-2">
                  {recentAudits.map((a: any) => (
                    <div key={a.id} className="flex items-center justify-between text-xs p-2 rounded border">
                      <div className="min-w-0">
                        <p className="font-medium capitalize">{a.listing_type}</p>
                        <p className="text-muted-foreground">{format(new Date(a.audited_at), 'MMM d, HH:mm')}</p>
                      </div>
                      <Badge variant={a.status === 'passed' ? 'default' : a.status === 'rejected' ? 'destructive' : 'secondary'} className="text-[10px]">
                        {a.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {isLead && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Team Performance (Week)</CardTitle>
              </CardHeader>
              <CardContent>
                {!teamPerf?.length ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No activity this week</p>
                ) : (
                  <div className="space-y-2">
                    {teamPerf.map(m => (
                      <div key={m.id} className="flex items-center justify-between text-xs">
                        <span className="font-medium">{m.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">{m.passed}/{m.total}</span>
                          <Badge variant="outline" className="text-[10px]">{Math.round((m.passed / m.total) * 100)}%</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
